/**
 * Workflows (automation engine) — shared DTO contract + the SAFE allowlists.
 *
 * The trigger types, action types, and the person-field allowlist live here so
 * the API engine and the web builder agree on exactly what is expressible. The
 * API is the security boundary: it re-validates every trigger/action/config
 * against these allowlists before persisting or executing, and the webhook
 * action additionally validates the URL server-side to prevent SSRF.
 */

// ----------------------------------------------------------------------------
// Triggers
// ----------------------------------------------------------------------------

/**
 * Event-driven triggers fired by the source verticals, plus a time-based
 * `schedule` trigger and a `manual` trigger (only runs via /test or explicit
 * enqueue). The dispatcher matches a fired event to enabled workflows whose
 * trigger.type equals the event type and whose config filters (if any) pass.
 */
export const WORKFLOW_TRIGGER_TYPES = [
  'manual',
  'signup.created',
  'list.member_added',
  'form.submitted',
  'schedule',
] as const

export type WorkflowTriggerType = (typeof WORKFLOW_TRIGGER_TYPES)[number]

/** Schedule cadence for the `schedule` trigger. `cron` carries an opaque expr. */
export type WorkflowScheduleKind = 'daily' | 'weekly' | 'monthly' | 'cron'

export interface WorkflowTrigger {
  type: WorkflowTriggerType
  /**
   * Type-specific config. Optional filters narrow which events match:
   *  - signup.created   : { eventId? }
   *  - list.member_added: { listId? }
   *  - form.submitted   : { formId? }
   *  - schedule         : { kind, at?, dayOfWeek?, dayOfMonth?, cron? }
   */
  config: Record<string, unknown>
}

// ----------------------------------------------------------------------------
// Actions (allowlisted)
// ----------------------------------------------------------------------------

export const WORKFLOW_ACTION_TYPES = [
  'add_to_list',
  'remove_from_list',
  'update_person_field',
  'send_notification',
  'webhook',
] as const

export type WorkflowActionType = (typeof WORKFLOW_ACTION_TYPES)[number]

/**
 * Person fields an `update_person_field` action may write. This is a STRICT
 * allowlist enforced server-side: only safe, non-identity scalar fields. PII
 * keys, ids, org_id, and structural columns are intentionally excluded.
 */
export const UPDATABLE_PERSON_FIELDS = [
  'membershipStatus',
  'householdRole',
  'maritalStatus',
  'gender',
] as const

export type UpdatablePersonField = (typeof UPDATABLE_PERSON_FIELDS)[number]

/** Human labels + allowed values for the builder UI (also enforced server-side). */
export interface UpdatablePersonFieldDef {
  key: UpdatablePersonField
  label: string
  options: { value: string; label: string }[]
}

export const UPDATABLE_PERSON_FIELD_DEFS: UpdatablePersonFieldDef[] = [
  {
    key: 'membershipStatus',
    label: 'Membership status',
    options: [
      { value: 'visitor', label: 'Visitor' },
      { value: 'regular', label: 'Regular' },
      { value: 'member', label: 'Member' },
      { value: 'inactive', label: 'Inactive' },
    ],
  },
  {
    key: 'householdRole',
    label: 'Household role',
    options: [
      { value: 'head', label: 'Head' },
      { value: 'spouse', label: 'Spouse' },
      { value: 'child', label: 'Child' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    key: 'maritalStatus',
    label: 'Marital status',
    options: [
      { value: 'single', label: 'Single' },
      { value: 'married', label: 'Married' },
      { value: 'widowed', label: 'Widowed' },
      { value: 'divorced', label: 'Divorced' },
      { value: 'separated', label: 'Separated' },
    ],
  },
  {
    key: 'gender',
    label: 'Gender',
    options: [
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
    ],
  },
]

export const UPDATABLE_PERSON_FIELD_BY_KEY: Record<string, UpdatablePersonFieldDef> =
  Object.fromEntries(UPDATABLE_PERSON_FIELD_DEFS.map((f) => [f.key, f]))

/** Engine safety caps (mirrored server-side; documented for the builder). */
export const MAX_ACTIONS_PER_WORKFLOW = 25
/** Max actions the runner will execute in a single workflow run (loop guard). */
export const MAX_ACTIONS_PER_RUN = 50

// ----------------------------------------------------------------------------
// DTOs
// ----------------------------------------------------------------------------

export interface WorkflowActionDto {
  id: string
  type: WorkflowActionType
  config: Record<string, unknown>
  position: number
}

export interface WorkflowSummary {
  id: string
  name: string
  description: string | null
  enabled: boolean
  trigger: WorkflowTrigger
  actionCount: number
  lastRunAt: string | null
  createdAt: string
  updatedAt: string
}

export interface WorkflowDetail extends WorkflowSummary {
  definition: Record<string, unknown>
  actions: WorkflowActionDto[]
  createdByUserId: string | null
}

/** An action as submitted by the builder (no id; the API assigns position). */
export interface WorkflowActionInput {
  type: WorkflowActionType
  config?: Record<string, unknown>
}

export interface NewWorkflowInput {
  name: string
  description?: string | null
  enabled?: boolean
  trigger: WorkflowTrigger
  actions?: WorkflowActionInput[]
  definition?: Record<string, unknown>
}

export interface UpdateWorkflowInput {
  name?: string
  description?: string | null
  enabled?: boolean
  trigger?: WorkflowTrigger
  actions?: WorkflowActionInput[]
  definition?: Record<string, unknown>
}

export type WorkflowRunStatus = 'succeeded' | 'failed' | 'skipped'
export type WorkflowStepStatus = 'ok' | 'skipped' | 'error'

export interface WorkflowStepResult {
  type: WorkflowActionType
  status: WorkflowStepStatus
  detail: string
}

export interface WorkflowRunDto {
  id: string
  workflowId: string
  status: WorkflowRunStatus
  triggerPayload: Record<string, unknown>
  steps: WorkflowStepResult[]
  dryRun: boolean
  error: string | null
  startedAt: string
  finishedAt: string | null
}

/** Result of POST /workflows/:id/test — a dry-run that applies no side effects. */
export interface WorkflowTestResult {
  status: WorkflowRunStatus
  steps: WorkflowStepResult[]
  error: string | null
}
